/**
 * Login Component
 *
 * Presentational student-login view: renders the form and maps
 * {@link AuthService} results to UI state. The instructor flow and lockout
 * countdown live in the `<qd-instructor-login>` and `<qd-lockout-banner>`
 * child components. Release is read from the document title.
 *
 * @element qd-login
 * @fires {CustomEvent<{serviceId,name,release,role}>} qd:login - On successful auth
 */

import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { loginStyles } from './qd-login.styles.js';
import { hasActiveSession, isInstructor } from '../utils/session-state.js';
import { STORAGE_KEYS, SESSION_TIMEOUT_MS } from '../types/contracts.js';
import {
  validateStudentForm,
  validateStudentFields,
  sanitizePinInput,
  type StudentFieldErrors,
} from '../utils/validation-helpers.js';
import { SessionService } from '../services/session.js';
import { readRelease, readDbName } from '../config/dom-config-reader.js';
import { AuthService } from '../services/auth/auth-service.js';
import type { LoginResult } from '../services/auth/auth-service.js';
import './qd-build-info.js';
import './qd-instructor-login.js';
import './qd-lockout-banner.js';
import './qd-help-trigger.js';
import './qd-help-popup.js';
import './qd-migration-dialog.js';
import { getHelpContent } from '../config/help-content.js';
import { StorageFormatError } from '../services/storage/adapter-utils.js';

/** Login event data */
interface LoginData {
  serviceId: string;
  name: string;
  release: string;
  role: 'student' | 'instructor';
}

/** Login component for student and instructor authentication */
@customElement('qd-login')
export class QdLogin extends LitElement {
  /** Title text (configurable via init()) */
  @property({ type: String })
  title = 'Sonar Quiz System';

  /** Form field: Student name */
  @state() private name = '';
  /** Form field: Service ID (2-10 alphanumeric) */
  @state() private serviceId = '';
  /** Error message to display */
  @state() private errorMessage = '';
  /** Whether the form is currently submitting */
  @state() private isSubmitting = false;
  /** PIN input */
  @state() private pin = '';
  /** Epoch milliseconds when the current lockout ends (0 = not locked) */
  @state() private lockoutUntil = 0;
  /**
   * Fields the student has typed in or left, so validation only reports on
   * fields they have touched (SRD 013 UX-10). Submitting touches all three.
   */
  @state() private touched: ReadonlySet<'name' | 'serviceId' | 'pin'> = new Set();
  /**
   * Whether the entered service ID already has an account.
   * `null` means not looked up yet, or the lookup failed.
   */
  @state() private isRegistered: boolean | null = null;
  /** First PIN entry, held while the user re-types it to confirm (create flow) */
  @state() private pendingPin = '';
  /** Whether the help popup is open */
  @state() private helpOpen = false;
  /** Why the student was logged out, shown above the form (SRD 013 SEC-10) */
  @state() private sessionNotice = '';
  /** Whether the migration dialog is shown */
  @state() private showMigrationDialog = false;
  /** Storage format error that triggered the migration dialog */
  @state() private migrationError: StorageFormatError | null = null;
  /** Pending login data to retry after migration */
  @state() private pendingLoginData: {
    serviceId: string;
    name: string;
    release: string;
    pin: string;
    dbName: string;
  } | null = null;

  /** Student authentication service (storage/crypto/rate-limit logic) */
  private authService = new AuthService();

  /** Pending debounce for the "is this service ID known?" lookup */
  private registrationTimer?: number;

  static styles = loginStyles;

  connectedCallback() {
    super.connectedCallback();
    this.updateVisibility();
    this.takeSessionExpiredNotice();
    document.addEventListener('qd:logout', this.handleLogoutEvent);
    // Instructor login is emitted by the child <qd-instructor-login>; hide on it.
    document.addEventListener('qd:login', this.handleLoginEvent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.registrationTimer);
    document.removeEventListener('qd:logout', this.handleLogoutEvent);
    document.removeEventListener('qd:login', this.handleLoginEvent);
  }

  /** Hide the form whenever a login succeeds (student or instructor). */
  private handleLoginEvent = (): void => {
    this.sessionNotice = '';
    this.updateVisibility();
  };

  /**
   * Show, once, why the last session ended if it timed out. The marker is
   * written by the session coordinator before it clears the session.
   */
  private takeSessionExpiredNotice(): void {
    if (sessionStorage.getItem(STORAGE_KEYS.SESSION_EXPIRED) === null) {
      return;
    }
    sessionStorage.removeItem(STORAGE_KEYS.SESSION_EXPIRED);
    const minutes = Math.round(SESSION_TIMEOUT_MS / 60000);
    this.sessionNotice = `Your session ended after ${minutes} minutes without activity. Log in again to continue.`;
  }

  /** Lifecycle: signal readiness once the shadow DOM is rendered. */
  firstUpdated() {
    this.setAttribute('data-ready', '');
  }

  /**
   * Show the form only when nobody is logged in. An instructor session hides
   * it too, so the form and the instructor panel never render together
   * (SRD 013 SEC-05).
   */
  private updateVisibility(): void {
    this.toggleAttribute('data-show', !hasActiveSession() && !isInstructor());
  }

  /** On logout, reset state and show the login form again. */
  private handleLogoutEvent = (): void => {
    this.name = '';
    this.serviceId = '';
    this.errorMessage = '';
    this.isSubmitting = false;
    this.pin = '';
    this.lockoutUntil = 0;
    this.touched = new Set();
    this.helpOpen = false;
    this.isRegistered = null;
    this.pendingPin = '';
    this.takeSessionExpiredNotice();
    this.updateVisibility();
  };

  /** Whether the account is currently within its lockout window. */
  private isLockedOut(): boolean {
    return this.lockoutUntil > Date.now();
  }

  /** Clear the lockout once the banner's countdown ends. */
  private handleLockoutExpired = (): void => {
    this.lockoutUntil = 0;
  };

  render() {
    return html`
      <div class="login-container">
        <div class="title">
          ${this.title}
          <qd-build-info></qd-build-info>
          <qd-help-trigger panelType="login" @qd:help-open=${this.handleHelpOpen}></qd-help-trigger>
        </div>

        ${this.sessionNotice
          ? html`<div class="notice-message" role="status">${this.sessionNotice}</div>`
          : ''}
        <form class="login-form" @submit=${(e: Event) => this.handleStudentLogin(e)}>
          <input
            type="text"
            name="name"
            placeholder="Name (J Smith)"
            aria-label="Name"
            aria-invalid=${this.fieldErrors.name ? 'true' : 'false'}
            .value=${this.name}
            @input=${(e: Event) => this.handleNameInput(e)}
            @blur=${() => this.touch('name')}
            ?disabled=${this.isSubmitting}
            required
          />
          <input
            type="text"
            name="serviceId"
            placeholder="Service ID (30012345)"
            aria-label="Service ID"
            aria-invalid=${this.fieldErrors.serviceId ? 'true' : 'false'}
            .value=${this.serviceId}
            @input=${(e: Event) => this.handleServiceIdInput(e)}
            @blur=${() => this.touch('serviceId')}
            ?disabled=${this.isSubmitting}
            pattern="[A-Za-z0-9]{2,10}"
            title="2-10 alphanumeric characters"
            required
          />
          <input
            type="password"
            name="pin"
            class="pin-input"
            placeholder=${this.pendingPin ? 'Re-enter PIN' : 'PIN (4 digits)'}
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="4"
            autocomplete="off"
            aria-label="Enter your 4-digit PIN"
            aria-invalid=${this.fieldErrors.pin ? 'true' : 'false'}
            .value=${this.pin}
            @input=${(e: Event) => this.handlePinInput(e)}
            @blur=${() => this.touch('pin')}
            ?disabled=${this.isSubmitting || this.isLockedOut()}
            required
          />
          <button
            type="submit"
            class="login-btn"
            ?disabled=${this.isSubmitting || !this.isValid() || this.isLockedOut()}
          >
            ${this.submitLabel}
          </button>
          <qd-instructor-login ?disabled=${this.isSubmitting}></qd-instructor-login>
          <div class="message-slot">
            ${this.errorMessage
              ? html`<div class="error-message" role="alert">${this.errorMessage}</div>`
              : this.validationHint
                ? html`<div class="hint-message" role="status">${this.validationHint}</div>`
                : ''}
          </div>
          <qd-lockout-banner
            .untilMs=${this.lockoutUntil}
            @qd:lockout-expired=${this.handleLockoutExpired}
          ></qd-lockout-banner>
        </form>
      </div>
      <qd-help-popup
        .open=${this.helpOpen}
        .title=${getHelpContent('login').title}
        .content=${getHelpContent('login').body}
        @qd:modal-close=${this.handleHelpClose}
      ></qd-help-popup>
      <qd-migration-dialog
        .open=${this.showMigrationDialog}
        .expected=${this.migrationError?.expected ?? 'plain'}
        .found=${this.migrationError?.found ?? 'plain'}
        .dbName=${this.pendingLoginData?.dbName ?? ''}
        .releaseId=${this.pendingLoginData?.release ?? ''}
        @qd:migration-complete=${this.handleMigrationComplete}
        @qd:migration-cancel=${this.handleMigrationCancel}
      ></qd-migration-dialog>
    `;
  }

  private handleHelpOpen = (): void => {
    this.helpOpen = true;
  };
  private handleHelpClose = (): void => {
    this.helpOpen = false;
  };

  private handleNameInput(e: Event) {
    this.name = (e.target as HTMLInputElement).value;
    this.errorMessage = '';
    this.touch('name');
  }
  private handleServiceIdInput(e: Event) {
    this.serviceId = (e.target as HTMLInputElement).value;
    this.errorMessage = '';
    this.touch('serviceId');
    // The identity changed, so a PIN captured for the previous one is void
    this.pendingPin = '';
    this.scheduleRegistrationCheck();
  }

  /**
   * Debounced lookup of whether the entered service ID already has an account,
   * used to label the submit button "Login" or "Create".
   */
  private scheduleRegistrationCheck(): void {
    window.clearTimeout(this.registrationTimer);
    this.isRegistered = null;

    const serviceId = this.serviceId.trim();
    if (!/^[a-zA-Z0-9]{2,10}$/.test(serviceId)) {
      return;
    }

    this.registrationTimer = window.setTimeout(() => {
      void this.checkRegistration(serviceId);
    }, 300);
  }

  /** Resolve {@link isRegistered} for one service ID, ignoring stale answers. */
  private async checkRegistration(serviceId: string): Promise<void> {
    let known: boolean | null = null;
    try {
      const release = readRelease();
      known = release
        ? await this.authService.isRegistered(serviceId, release, readDbName())
        : null;
    } catch {
      known = null;
    }
    // Discard a late answer for a service ID the user has since edited
    if (serviceId === this.serviceId.trim()) {
      this.isRegistered = known;
    }
  }

  /**
   * Label for the submit button.
   *
   * A first-time user sees "Create" rather than a "Login" that cannot succeed,
   * and "Confirm" while re-entering their new PIN. Unknown registration falls
   * back to "Login".
   */
  private get submitLabel(): string {
    if (this.pendingPin) {
      return 'Confirm';
    }
    return this.isRegistered === false ? 'Create' : 'Login';
  }
  private handlePinInput(e: Event) {
    // Filter to digits only using validation helper
    this.pin = sanitizePinInput((e.target as HTMLInputElement).value);
    this.errorMessage = '';
    this.touch('pin');
  }

  /** Mark a field as touched so its validation message may show. */
  private touch(field: 'name' | 'serviceId' | 'pin'): void {
    if (!this.touched.has(field)) {
      this.touched = new Set([...this.touched, field]);
    }
  }

  /** Validation messages for touched fields only. */
  private get fieldErrors(): StudentFieldErrors {
    const all = validateStudentFields(this.name, this.serviceId, this.pin);
    const shown: StudentFieldErrors = {};
    for (const field of this.touched) {
      if (all[field]) {
        shown[field] = all[field];
      }
    }
    return shown;
  }

  /** Whether the student form passes validation. */
  private isValid(): boolean {
    return validateStudentForm(this.name, this.serviceId, this.pin).length === 0;
  }

  /**
   * First outstanding validation problem, for display while the user types.
   *
   * The Login button is disabled until the form is valid, which on its own
   * leaves no way to discover what is missing: the submit handler's error
   * message is unreachable because submitting is exactly what is blocked.
   * This surfaces the same message live instead. A pristine form stays quiet
   * so the user is not scolded before typing anything.
   *
   * @returns The first validation error, or an empty string when the form is
   *   untouched or valid
   */
  private get validationHint(): string {
    if (this.pendingPin) {
      return 'Re-enter your PIN to confirm';
    }
    if (!this.name && !this.serviceId && !this.pin) {
      return '';
    }
    // Every outstanding problem at once, not just the first (UX-10), so the
    // disabled Login button is always explained. Only touched fields are
    // also marked invalid, so the student is not scolded field by field.
    return Object.values(validateStudentFields(this.name, this.serviceId, this.pin)).join(' · ');
  }

  /**
   * Handle student login: validate, resolve release/db-name, delegate to
   * {@link AuthService}, and apply the result (or open the migration dialog).
   */
  private async handleStudentLogin(e: Event) {
    e.preventDefault();

    if (!this.isValid()) {
      this.touched = new Set(['name', 'serviceId', 'pin']);
      this.errorMessage = 'Please enter name, service ID, and 4-digit PIN';
      return;
    }

    // The debounced lookup may not have answered yet (fast typist, fast click),
    // so settle it here rather than letting timing decide whether an account
    // is created with or without confirmation.
    if (this.isRegistered === null && !this.pendingPin) {
      await this.checkRegistration(this.serviceId.trim());
    }

    // Creating an account takes the PIN twice, so a typo cannot silently
    // become the stored PIN and lock the student out of their own record.
    if (this.pendingPin) {
      if (this.pin !== this.pendingPin) {
        this.pendingPin = '';
        this.pin = '';
        this.errorMessage = 'PINs did not match. Please enter your PIN again.';
        return;
      }
      this.pendingPin = '';
    } else if (this.isRegistered === false) {
      this.pendingPin = this.pin;
      this.pin = '';
      this.errorMessage = '';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    const release = readRelease();
    if (!release) {
      this.errorMessage = 'Release not found (missing publication title element)';
      this.isSubmitting = false;
      return;
    }

    const serviceId = this.serviceId.trim();
    const name = this.name.trim();
    const dbName = readDbName();
    const pin = this.pin;

    const result = await this.authService.loginStudent({ serviceId, name, pin, release, dbName });

    if (result.kind === 'needs-migration') {
      this.migrationError = result.error;
      this.pendingLoginData = { serviceId, name, release, pin, dbName };
      this.showMigrationDialog = true;
      this.isSubmitting = false;
      return;
    }

    this.applyLoginResult(result);
  }

  /** Map an {@link AuthService} result to session/events/UI state. */
  private applyLoginResult(result: LoginResult): void {
    switch (result.kind) {
      case 'pin-created':
        // No "PIN stored" dialog: the student has just confirmed the PIN and
        // the status panel shows they are in (SRD 013 UX-02)
        this.dispatchPinEvent('qd:pin-created', result.serviceId);
        this.completeLogin(result.serviceId, result.name, result.release);
        break;
      case 'pin-verified':
        this.dispatchPinEvent('qd:pin-verified', result.serviceId);
        this.completeLogin(result.serviceId, result.name, result.release);
        break;
      case 'lockout':
        this.lockoutUntil = Date.now() + result.lockoutMs;
        this.errorMessage = '';
        this.isSubmitting = false;
        break;
      case 'bad-pin':
        this.errorMessage = `Incorrect PIN. ${result.remaining} attempt${
          result.remaining !== 1 ? 's' : ''
        } remaining`;
        this.pin = '';
        this.isSubmitting = false;
        break;
      case 'error':
        this.errorMessage = result.message;
        this.isSubmitting = false;
        break;
    }
  }

  /** Dispatch a PIN lifecycle event (`qd:pin-created` / `qd:pin-verified`). */
  private dispatchPinEvent(name: 'qd:pin-created' | 'qd:pin-verified', serviceId: string): void {
    this.dispatchEvent(
      new CustomEvent(name, {
        detail: { serviceId, timestamp: new Date().toISOString() },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** On successful migration, retry login with the pending (validated) data. */
  private handleMigrationComplete = (): void => {
    this.showMigrationDialog = false;
    this.migrationError = null;

    if (this.pendingLoginData) {
      const { serviceId, name, release } = this.pendingLoginData;
      this.pendingLoginData = null;
      void this.retryLoginAfterMigration(serviceId, name, release);
    }
  };

  /** Retry login after migration (skips the lockout pre-check). */
  private async retryLoginAfterMigration(
    serviceId: string,
    name: string,
    release: string,
  ): Promise<void> {
    this.isSubmitting = true;
    this.errorMessage = '';

    const result = await this.authService.retryAfterMigration({
      serviceId,
      name,
      pin: this.pin,
      release,
      dbName: readDbName(),
    });

    this.applyLoginResult(result);
  }

  /** On migration cancel, show a contact-instructor message. */
  private handleMigrationCancel = (): void => {
    this.showMigrationDialog = false;
    this.migrationError = null;
    this.pendingLoginData = null;
    this.errorMessage = 'Data migration cancelled. Please contact your instructor for assistance.';
    this.isSubmitting = false;
  };

  /** Create the student session, emit `qd:login`, and hide the form. */
  private completeLogin(serviceId: string, name: string, release: string): void {
    new SessionService().createSession(serviceId, name, release);

    const loginData: LoginData = { serviceId, name, release, role: 'student' };
    this.dispatchEvent(
      new CustomEvent('qd:login', { detail: loginData, bubbles: true, composed: true }),
    );

    this.pin = '';
    this.isSubmitting = false;
    this.updateVisibility();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'qd-login': QdLogin;
  }
}
